import { ChangeEvent, useMemo } from 'react'

import { useAccount, useNetwork } from 'wagmi'
import { privateKeyToAccount } from 'viem/accounts'

import { Strategies } from 'types/entities'

import { useRequest } from 'hooks/api'
import { useIsWrongChainId } from 'hooks/wallet'
import { useAirdropper } from 'hooks/airdrop'

import {
  useStrategy,
  useSignature,
  useSetStrategy,
  useSetSignature,
  usePrepareTxsList,
  useIsSelfGenerated,
} from 'state/airdrop/hooks'
import { usePrivateKey, useSetPrivateKey, useSetIsPKSaved, useIsPKSaved } from 'state/user/hooks'
import { GetSelfGeneratedSignatureInput, useSelfGeneratedSignature } from 'hooks/airdrop/useSelfGeneratedSignature'

import { SelfGeneratedInfo } from 'components/features/SelfGeneratedInfo'
import { Button, Checkbox, Icon, IconMap, Notification, Slash, Tooltip } from 'components/ui'

import { options } from './options'
import { SectionLoader } from './Loader'
import {
  Box,
  Radio,
  Wrapper,
  ItemIcon,
  Container,
  ItemLabel,
  LabelTitle,
  ItemContent,
  ItemTooltip,
  ButtonsWrap,
  ContainerLabel,
  ItemContentText,
  ItemContentList,
  ItemContentListItem,
} from './Style'

enum BtnText {
  CONTINUE = '_continue',
  SIGN_KEY = '_sign-key',
}

type Props = {
  isDisabled: boolean
  onStrategyBlockToggle: (value: boolean) => void
}

export function StrategySelector({ isDisabled, onStrategyBlockToggle }: Props) {
  const strategy = useStrategy()
  const setStrategy = useSetStrategy()

  const { address } = useAccount()
  const network = useNetwork()
  const airdropper = useAirdropper()
  const prepareTxsList = usePrepareTxsList()

  const privateKey = usePrivateKey()
  const setPrivateKey = useSetPrivateKey()

  const signature = useSignature()
  const setIsPKSaved = useSetIsPKSaved()
  const setSignature = useSetSignature()
  const isPKSaved = useIsPKSaved()
  const isWrongChainId = useIsWrongChainId()
  const isSelfGenerated = useIsSelfGenerated()
  const getSelfGeneratedSignature = useSelfGeneratedSignature()

  const shouldSignKey = useMemo(() => {
    return isSelfGenerated && (!privateKey || !signature)
  }, [privateKey, isSelfGenerated, signature])

  const onSelectStrategy = useRequest(async () => {
    await prepareTxsList()
    onStrategyBlockToggle(false)
  })

  const onGetSelfGeneratedSignature = useRequest(
    async ({ address, selfGeneratedAddress }: GetSelfGeneratedSignatureInput) => {
      const signature = await getSelfGeneratedSignature({ address, selfGeneratedAddress })
      setSignature(signature)
    },
  )

  const onContinue = () => {
    onSelectStrategy.call()
  }

  const onGeneratePK = () => {
    let _privateKey = privateKey
    if (!privateKey) {
      _privateKey = generatePk()
    }

    if (_privateKey && !signature && address) {
      const pkAccount = privateKeyToAccount(_privateKey)
      onGetSelfGeneratedSignature.call({ address, selfGeneratedAddress: pkAccount.address })
    }
  }

  const generatePk = () => {
    const { privateKey } = airdropper.createSelfGeneratedAccount()
    setPrivateKey(privateKey)
    return privateKey
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStrategy(e.target.value as Strategies)
  }

  const showCheckbox = useMemo(() => privateKey && isSelfGenerated, [privateKey, isSelfGenerated])

  const _isDisabled = useMemo(() => {
    if (showCheckbox) {
      return isDisabled || isWrongChainId || !isPKSaved
    }

    return isDisabled || isWrongChainId
  }, [isDisabled, isWrongChainId, isPKSaved, showCheckbox])

  return (
    <Container>
      <LabelTitle>
        <Slash />
        _options-for-sending-transacations
      </LabelTitle>
      {onSelectStrategy.isLoading && <SectionLoader />}

      {onSelectStrategy.errorMessage && (
        <Notification type='error' margin='0 auto 1.6rem' message={onSelectStrategy.errorMessage} />
      )}
      <Wrapper>
        <ContainerLabel>
          {options.map((option) => (
            <ItemLabel key={option.value} htmlFor={option.value} isActive={strategy === option.value}>
              <Radio
                type='radio'
                id={option.value}
                name={option.value}
                value={option.value}
                onChange={handleChange}
                checked={strategy === option.value}
              />

              <ItemIcon>
                <Icon name={option.icon as IconMap} size='fill' />
              </ItemIcon>

              {option.label()}

              <ItemTooltip id={option.icon}>?</ItemTooltip>
              <Tooltip id={option.icon}>{option.tooltip()}</Tooltip>
            </ItemLabel>
          ))}
        </ContainerLabel>

        {isSelfGenerated && (
          <ItemContent>
            <ItemContentText>This is&nbsp;the fastest recommended way to&nbsp;use _mad-assembly.</ItemContentText>
            <ItemContentList>
              <ItemContentListItem>
                Click below to&nbsp;sign the _mad-assembly self-generated key request directly from your wallet.
              </ItemContentListItem>
              <ItemContentListItem>
                _mad-assembly will generate a&nbsp;burner key on&nbsp;your device, after which you can proceed
                to&nbsp;approve funds for sending transactions.
              </ItemContentListItem>
              <ItemContentListItem>
                Once the burner key receives the funds, it&nbsp;will automatically post all transactions to&nbsp;the{' '}
                {network.chain?.name} blockchain.
              </ItemContentListItem>
            </ItemContentList>

            {showCheckbox && (
              <>
                <SelfGeneratedInfo />
                <Box htmlFor='savePrivateKey'>
                  <Checkbox checked={isPKSaved} id='savePrivateKey' name='savePrivateKey' onClick={setIsPKSaved} />
                  <span>I saved private key</span>
                </Box>
              </>
            )}
          </ItemContent>
        )}
      </Wrapper>

      <ButtonsWrap>
        {shouldSignKey ? (
          <Button
            fullSize
            size='large'
            onClick={onGeneratePK}
            disabled={_isDisabled}
            isLoading={onGetSelfGeneratedSignature.isLoading}
          >
            {BtnText.SIGN_KEY}
          </Button>
        ) : (
          <Button
            fullSize
            size='large'
            onClick={onContinue}
            disabled={_isDisabled}
            isLoading={onSelectStrategy.isLoading}
          >
            {BtnText.CONTINUE}
          </Button>
        )}
      </ButtonsWrap>
    </Container>
  )
}
